import React from 'react';
import { IoOpenOutline } from 'react-icons/io5';

import { Button, Group, Stack, Text } from '@mantine/core';

import { IntlHelpers } from 'helpers/IntlHelpers';

import { InAppSupportBadge } from 'components/badges/InAppSupportBadge';
import { DatasheetMode, useDatasheetMode } from 'components/datasheet/DatasheetMode';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { useDistributorPrice } from 'hooks/use-distributor-price';
import { DateService } from 'services/DateService';
import { InternalTrackingService } from 'services/InternalTrackingService';

import type { Manufacturer } from '@/payload-types';

import { useComponentContext } from '../../hooks/use-component-context';

import cx from './Pricing.module.scss';

const Pricing = () => {
    const mode = useDatasheetMode();
    const { component } = useComponentContext();
    const { company: manufacturer } = useCompanyProfile(component.manufacturer);
    const { distributorPrice } = useDistributorPrice(component.id);

    if (mode !== DatasheetMode.VIEW) {
        return null;
    }

    if (distributorPrice) {
        const distributor = distributorPrice.distributor as Manufacturer;
        const distributorName = distributor?.name;

        return (
            <PricingCard
                label={distributorName ? `Distributed by ${distributorName}` : 'Distributor price'}
                price={IntlHelpers.formatPrice(distributorPrice.price)}
                leadTime={distributorPrice.leadTime}
                action={
                    distributorPrice.url && (
                        <Button
                            component="a"
                            href={distributorPrice.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                            rightSection={<IoOpenOutline size={14} />}
                            {...InternalTrackingService.link(
                                'product.distributor-price.cta',
                                'View distributor pricing',
                            )}
                        >
                            {distributorName ? `View at ${distributorName}` : 'View listing'}
                        </Button>
                    )
                }
            />
        );
    }

    const msrp = component.msrp ?? null;
    const leadTime = component.leadTime ?? null;

    if (msrp === null && leadTime === null) {
        return null;
    }

    return (
        <PricingCard
            label="Avg. price"
            price={msrp === null ? 'On request' : IntlHelpers.formatPrice(msrp)}
            leadTime={leadTime === null ? undefined : DateService.formatNumberOfDays(leadTime)}
            leadTimeLabel="Typ. lead time"
            action={
                msrp === null &&
                manufacturer && (
                    <InAppSupportBadge
                        company={manufacturer}
                        component={component}
                        label={`Contact ${manufacturer.name}`}
                        size="sm"
                    />
                )
            }
        />
    );
};

const PricingCard = ({
    label,
    price,
    leadTime,
    leadTimeLabel = 'Lead time',
    action,
}: {
    label: string;
    price?: string;
    leadTime?: string | null;
    leadTimeLabel?: string;
    action?: React.ReactNode;
}) => (
    <Group className={cx.card} justify="space-between" align="center" gap="md">
        <Stack gap={2}>
            <Text fz="sm" fw={500} c="dimmed">
                {label}
            </Text>
            {price && (
                <Text className={cx.price} fw={800}>
                    {price}
                </Text>
            )}
            {leadTime && (
                <Text fz="sm">
                    <Text inherit span c="dimmed">
                        {leadTimeLabel}:
                    </Text>{' '}
                    <Text inherit span fw={600}>
                        {leadTime}
                    </Text>
                </Text>
            )}
        </Stack>
        {action}
    </Group>
);

export { Pricing };
